import React, { useState } from 'react';
import { Currency, formatMoney } from '../../common/format';
import { RequestResponseViewer } from '../../common/request-response-viewer';
import {
  COMMON_PROJECTION_COLUMNS,
  ChartSeriesConfig,
  CurrencyHeader,
  EvolutionChart,
  KpiCard,
  KpiGrid,
  ProjectionColumn,
  ProjectionTable,
  buildEvolutionSeries,
} from '../_shared';
import { InsuredUniversityResponse, UniversityPolicyRow } from './service';

const CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
  { dataKey: 'accumulated_prime', name: 'Prima acumulada', stroke: 'var(--bs-gray-600)' },
  {
    dataKey: 'death_compensation',
    name: 'Indemnización fallecimiento',
    stroke: 'var(--bs-success)',
    strokeDasharray: '5 4',
  },
  {
    dataKey: 'withdrawal',
    name: 'Retiro universitario',
    stroke: 'var(--bs-warning)',
  },
];

const UNIVERSITY_COLUMNS: ProjectionColumn<UniversityPolicyRow>[] = [
  ...COMMON_PROJECTION_COLUMNS,
  { id: 'withdrawal', label: 'Retiro', isMoney: true },
  { id: 'policy_value_before_withdrawal', label: 'VP pre-retiro', isMoney: true },
  { id: 'policy_value_after_withdrawal', label: 'VP post-retiro', isMoney: true },
];

interface InsuredUniversityResultViewProps {
  request: unknown;
  response?: InsuredUniversityResponse;
  error?: unknown;
}

export function InsuredUniversityResultView({
  request,
  response,
  error,
}: InsuredUniversityResultViewProps) {
  const [currency, setCurrency] = useState<Currency>('UF');
  const hasSuccess = !!response && !error;

  return (
    <div className="mt-4">
      {hasSuccess && response && (
        <>
          <CurrencyHeader
            ufValue={response.uf_value}
            currency={currency}
            onCurrencyChange={setCurrency}
          />

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Ahorro bruto"
                value={formatMoney(response.savings.gross, currency, response.uf_value)}
                hint={`${response.timeframe_months} meses`}
                accent="text-primary"
              />
              <KpiCard
                label="Ahorro neto"
                value={formatMoney(response.savings.net, currency, response.uf_value)}
                accent="text-success"
              />
              <KpiCard
                label="Ganado por interés"
                value={formatMoney(response.savings.interest, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima acordada"
                value={formatMoney(response.premium.agreed, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima básica"
                value={formatMoney(response.premium.basic, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima excedente"
                value={formatMoney(response.premium.excess, currency, response.uf_value)}
                accent="text-danger"
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <EvolutionChart
              data={buildEvolutionSeries(
                response.projection_by_year,
                [
                  'policy_value',
                  'accumulated_prime',
                  'death_compensation',
                  'withdrawal',
                ],
                currency,
                response.uf_value,
              )}
              series={CHART_SERIES}
              currency={currency}
            />
          </div>
          <div className="mb-4">
            <ProjectionTable
              rows={response.projection_by_year}
              columns={UNIVERSITY_COLUMNS}
              currency={currency}
              ufValue={response.uf_value}
            />
          </div>
        </>
      )}

      <RequestResponseViewer request={request} response={response} error={error} />
    </div>
  );
}
